import { HotelsAvailabilityActions } from "@b2bportal/core-hotels";
import {
  type PackagesAutocompleteResult,
  type Requisition,
  type InitialLodgingAvailabilityResponse,
  PackageEndpointsV2,
} from "@b2bportal/core-packages";
import type { SearchDetails } from "@b2bportal/core-types";
import { getDeviceType } from "@b2bportal/core-utilities";
import {
  type Suggestion,
  LodgingSelectionEnum,
  PlaceIdEnum,
} from "@b2bportal/lodging-api";
import { axiosInstance } from "@hopper-b2b/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import { PackagesBaseActions } from "../../slice/packages";
import type { TPackagesByLodgingId } from "../../types/packagesHotels";
import type { PackagesSearch } from "../../types/packagesSearch";
import { fetchPackageHotelAvailabilityMore } from "./fetchPackageHotelAvailabilityMore";

/**
 * Converts a packages autocomplete destination (with lodgingsId.lodgingSelection)
 * into a Suggestion for the hotels availability search (SearchDetails.place).
 */
export const packagesDestinationToSuggestion = (
  destination: PackagesAutocompleteResult
): Suggestion => {
  const { lodgingsId } = destination;
  return {
    label: destination.label,
    id: {
      Id: PlaceIdEnum.Lodgings,
      lodgingSelection: lodgingsId.lodgingSelection,
    },
    trackingPropertiesV2: destination.trackingPropertiesV2,
  };
};

export const fetchPackageHotelAvailability = createAsyncThunk<
  TPackagesByLodgingId,
  { search: PackagesSearch; requisition: Requisition },
  { rejectValue: { error: string } }
>("packages/fetchHotels", async ({ search, requisition }, thunkAPI) => {
  const { isDesktopAndUp } = getDeviceType();
  const searchId = uuidv4();

  try {
    const hotelsAvailabilitySearch = {
      ...search,
      rooms: search.roomCount,
      guests: {
        adults: search.guests.adultCount,
        children: search.guests.childAges,
      },
      place: packagesDestinationToSuggestion(search.destination),
      searchType: LodgingSelectionEnum.Place,
      lodgingSelection: null,
    } satisfies SearchDetails;

    thunkAPI.dispatch(
      HotelsAvailabilityActions.setRequestedSearchFromPackages(
        hotelsAvailabilitySearch
      )
    );

    thunkAPI.dispatch(HotelsAvailabilityActions.handleFetchInitialPending());

    const {
      data: initialResponse,
    }: { data: InitialLodgingAvailabilityResponse } = await axiosInstance.post(
      PackageEndpointsV2.Requisition,
      {
        requisition: requisition,
      }
    );

    // Set benchmark products
    const initialBenchmarkProducts = initialResponse.chosenProducts;
    thunkAPI.dispatch(
      PackagesBaseActions.setBenchmarkProducts({
        chosenProducts: initialBenchmarkProducts,
      })
    );

    const initialLodgingAvailability =
      initialResponse.lodgingAvailability.payload;

    // Extract the actual stay dates from the availability response
    const availabilityDates = initialLodgingAvailability.availability.dates;

    thunkAPI.dispatch(
      HotelsAvailabilityActions.handleFetchInitialFulfilled({
        ...initialLodgingAvailability.availability,
        searchId,
      })
    );

    // Since we're early in the funnel, the token is the same for all hotels, grab the first and assign it as the base.
    const initialPackageState =
      initialResponse.lodgingAvailability.packageStateByLodgingId[
        initialResponse.lodgingAvailability.payload.availability.lodgings?.[0]
          ?.lodging?.id
      ] ??
      // Fallback to the first package state if no package state is found for the first lodging, usually  means we're still "searching" for hotels
      Object.values(
        initialResponse.lodgingAvailability.packageStateByLodgingId
      )[0];

    if (initialPackageState) {
      thunkAPI.dispatch(
        PackagesBaseActions.setCurrentActivePackage(initialPackageState)
      );
    }

    if (
      initialPackageState &&
      initialResponse.lodgingAvailability.payload.availability.nextPageToken
    ) {
      thunkAPI.dispatch(
        fetchPackageHotelAvailabilityMore({
          nextPageToken:
            initialResponse.lodgingAvailability.payload.availability
              .nextPageToken,
          searchId,
          packageStateToken: initialPackageState.token.value,
        })
      );
    }

    return {
      packagesByLodgingId:
        initialResponse.lodgingAvailability.packageStateByLodgingId,
      searchId,
      dateRange: availabilityDates,
    };
  } catch (e) {
    console.error("Failed to fetch hotels:", e);
    const errorMessage = e instanceof Error ? e.message : String(e);
    thunkAPI.dispatch(
      HotelsAvailabilityActions.handleFetchInitialRejected({
        error: errorMessage,
      })
    );
    return thunkAPI.rejectWithValue({ error: errorMessage });
  }
});
